import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

const StatCard = ({ icon, color, label, value, sub, children, onClick }) => (
  <div className="tl-card" style={{ borderLeft: `4px solid ${color}`, cursor: onClick ? "pointer" : "default", padding: "20px 24px" }} onClick={onClick}>
    <div className="d-flex align-items-center gap-2 mb-2">
      <div style={{ width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, backgroundColor: color + "20", color }}>{icon}</div>
      <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--tl-text-muted)" }}>{label}</div>
    </div>
    <div style={{ fontSize: 36, fontWeight: 700, lineHeight: 1.1 }}>{typeof value === "number" ? value.toLocaleString() : value}</div>
    {sub && <div style={{ fontSize: 13, color: "var(--tl-text-muted)", marginTop: 4 }}>{sub}</div>}
    {children}
  </div>
);

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productsRes, analyticsRes, scansTodayRes] = await Promise.all([
          api.get("/api/products"),
          api.get("/api/analytics/devices"),
          api.get("/api/analytics/scans-today"),
        ]);

        const products = productsRes.data;
        const totalProducts = products.length;
        const totalUrls = products.reduce((s, p) => s + (p.urls?.length || 0), 0);
        const totalClicks = products.reduce((s, p) => s + (p.totalClicks || 0), 0);
        const withQr = products.filter((p) => p.urls?.length > 0).length;
        const withoutQr = totalProducts - withQr;

        // Count by format
        let legacy = 0, standard = 0, gs1 = 0, deprecated = 0;
        const retailers = new Set();
        for (const p of products) {
          if (p.retailer) retailers.add(p.retailer);
          for (const u of (p.urls || [])) {
            if (u.qrFormat === "legacy") legacy++;
            else if (u.qrFormat === "gs1") gs1++;
            else standard++;
            if (u.isDeprecated) deprecated++;
          }
        }

        // Top products
        const topProducts = [...products]
          .sort((a, b) => (b.totalClicks || 0) - (a.totalClicks || 0))
          .slice(0, 15);

        // By retailer
        const byRetailer = {};
        for (const p of products) {
          const r = p.retailer || "[none]";
          if (!byRetailer[r]) byRetailer[r] = { products: 0, codes: 0, clicks: 0 };
          byRetailer[r].products++;
          byRetailer[r].codes += p.urls?.length || 0;
          byRetailer[r].clicks += p.totalClicks || 0;
        }
        const retailerList = Object.entries(byRetailer)
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.clicks - a.clicks);

        const topProduct = topProducts[0];
        const deviceData = analyticsRes.data;

        setStats({
          totalProducts, totalUrls, totalClicks, withQr, withoutQr,
          legacy, standard, gs1, deprecated,
          retailerCount: retailers.size,
          topProduct,
          topProducts,
          retailerList,
          mobilePercent: deviceData.devices?.total > 0 ? Math.round((deviceData.devices.mobile / deviceData.devices.total) * 100) : 0,
          scansToday: scansTodayRes.data.count || 0,
        });
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-white" /></div>;
  if (!stats) return null;

  return (
    <div>
      <div className="tl-card">
        <div className="tl-page-header"><h2>Dashboard</h2></div>
      </div>

      {/* Row 1 */}
      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <StatCard icon="📦" color="#6366f1" label="TOTAL PRODUCTS" value={stats.totalProducts}
            sub={`${stats.withQr} with QR codes · ${stats.withoutQr} without`}
            onClick={() => navigate("/products")} />
        </div>
        <div className="col-md-3">
          <StatCard icon="📱" color="#22c55e" label="TOTAL QR CODES" value={stats.totalUrls}
            sub={`${stats.legacy} legacy · ${stats.standard} standard · ${stats.gs1} GS1`} />
        </div>
        <div className="col-md-3">
          <StatCard icon="📊" color="#3b82f6" label="SCANS TODAY" value={stats.scansToday}
            sub={`${stats.totalClicks.toLocaleString()} all-time · ${stats.mobilePercent}% mobile`}
            onClick={() => navigate("/analytics")} />
        </div>
        <div className="col-md-3">
          <StatCard icon="⭐" color="#f59e0b" label="TOP PRODUCT"
            value={stats.topProduct ? stats.topProduct.totalClicks.toLocaleString() : "0"}
            sub={stats.topProduct?.productName || "—"} />
        </div>
      </div>

      {/* Row 2 */}
      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <StatCard icon="🏪" color="#8b5cf6" label="RETAILERS" value={stats.retailerCount} />
        </div>
        <div className="col-md-3">
          <StatCard icon="⚠️" color="#94a3b8" label="DEPRECATED CODES" value={stats.deprecated}
            sub="still redirect but excluded from downloads" />
        </div>
        <div className="col-md-3">
          <StatCard icon="🔗" color="#14b8a6" label="GS1 CODES" value={stats.gs1}
            sub="GS1 Digital Link format" />
        </div>
        <div className="col-md-3">
          <StatCard icon="📋" color="#ec4899" label="LEGACY CODES" value={stats.legacy}
            sub="migrated from original system" />
        </div>
      </div>

      {/* Row 3 — Tables */}
      <div className="row g-3">
        <div className="col-md-6">
          <div className="tl-card tl-card-compact" style={{ display: "flex", flexDirection: "column", maxHeight: "50vh" }}>
            <h6 style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", color: "var(--tl-text-muted)", marginBottom: 12, flexShrink: 0 }}>
              Top Products by Scans
            </h6>
            <div style={{ overflow: "auto", flex: 1 }}>
              <table className="tl-table">
                <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                  <tr><th>Product</th><th>Retailer</th><th style={{ textAlign: "right" }}>QR Codes</th><th style={{ textAlign: "right" }}>Scans</th></tr>
                </thead>
                <tbody>
                  {stats.topProducts.map((p, i) => (
                    <tr key={i} onClick={() => navigate(`/products?productName=${encodeURIComponent(p.productName)}`)}>
                      <td><span className="name-link">{p.productName}</span></td>
                      <td className="text-muted">{p.retailer || "—"}</td>
                      <td style={{ textAlign: "right" }}>{p.urls?.length || 0}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{(p.totalClicks || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="tl-card tl-card-compact" style={{ display: "flex", flexDirection: "column", maxHeight: "50vh" }}>
            <h6 style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", color: "var(--tl-text-muted)", marginBottom: 12, flexShrink: 0 }}>
              By Retailer
            </h6>
            <div style={{ overflow: "auto", flex: 1 }}>
              <table className="tl-table">
                <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                  <tr><th>Retailer</th><th style={{ textAlign: "right" }}>Products</th><th style={{ textAlign: "right" }}>QR Codes</th><th style={{ textAlign: "right" }}>Scans</th></tr>
                </thead>
                <tbody>
                  {stats.retailerList.map((r, i) => (
                    <tr key={i} onClick={() => navigate(`/products?retailer=${encodeURIComponent(r.name)}`)}>
                      <td><span className="name-link">{r.name}</span></td>
                      <td style={{ textAlign: "right" }}>{r.products}</td>
                      <td style={{ textAlign: "right" }}>{r.codes}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{r.clicks.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
